import { motion, useScroll } from 'framer-motion';
import { useRef, useState, useEffect, useCallback } from 'react';
import { MotionImage, useSanitizedTranslation, StackedContent, XumoButton } from 'ui';

import Image from 'next/image';

export interface TrackLogo {
  src: string;
  width: number;
  height: number;
}

export interface CarouselCTA {
  href: string;
  color?: string;
}

export interface CarouselItem {
  logo: TrackLogo;
  date: string;
  month: string;
  day: string;
  trackname: string;
  location: string;
  races: string[];
  cta?: CarouselCTA;
}

export interface TimelineCarouselItem {
  img: { src: string; alt: string };
  date: string;
  copy: string;
}

export interface CarouselProps {
  bg?: string;
  sectionClass?: string;
  title?: any;
  items: CarouselItem[];
}

const CarouselItem = ({ logo, month, day, trackname, location, races, cta }: CarouselItem) => {
  const { t } = useSanitizedTranslation();
  return (
    <div className="flex h-full w-max min-w-[325px] max-w-[325px] snap-center flex-col items-center justify-center overflow-hidden rounded-xl bg-white sm:min-w-[400px] sm:max-w-[400px]">
      {/* Image */}
      <div className="h-max w-full bg-[#DCE6F0] p-3">
        <StackedContent
          center
          noMargin
          minHeight
          content={[
            {
              image: {
                src: logo.src,
                width: logo.width,
                height: logo.height,
                renderedHeight: 150,
              },
            },
          ]}
        />
      </div>
      {/* Text */}
      <div className="flex w-full flex-col items-center justify-center gap-8 px-6 pb-12 pt-6">
        {/* Date and location */}
        <div className="flex w-full flex-row items-center justify-between gap-4">
          <div className="flex aspect-square min-h-[80px] min-w-[80px] flex-col items-center justify-center rounded-sm border border-[#DCE6F0] p-1 sm:min-h-[95px] sm:min-w-[95px]">
            <p className="uppercase">{t(month)}</p>
            <p className="text-xl sm:text-2xl">{t(day)}</p>
          </div>
          <div className="flex h-full w-full flex-col items-center justify-center border-l border-black py-5 pl-3">
            <p className="text-center">{t(trackname)}</p>
            <p className="text-center text-sm">{t(location)}</p>
          </div>
        </div>
        {/* Race */}
        <div className="flex w-3/4 flex-col items-center justify-center gap-3">
          {races.map((race, index) => (
            <div className="flex w-full items-center justify-center rounded-full bg-[#F6F8F9] p-3">
              <p key={index + '-race'} className="text-center font-medium">
                {race}
              </p>
            </div>
          ))}
        </div>
        {/* Button */}
        {cta?.href && <XumoButton label="View race recap" href={cta.href} secondary={true} color={cta.color ?? 'xumoTeal'} />}
      </div>
    </div>
  );
};

export const EventsCarousel = ({ bg, title, items, sectionClass = '' }: CarouselProps) => {
  const now = new Date();
  const sortedItems = [...items].sort((a, b) => {
    const aDate = new Date(a.date);
    const bDate = new Date(b.date);
    const aIsPast = aDate < now;
    const bIsPast = bDate < now;

    // Different buckets: past goes after upcoming
    if (aIsPast !== bIsPast) {
      return aIsPast ? 1 : -1;
    }

    // Same bucket: sort by date ascending
    return aDate.getTime() - bDate.getTime();
  });

  return (
    <motion.section className={`flex flex-col items-start justify-center ${sectionClass}`} style={{ background: bg ? bg : 'white' }}>
      {/* Title */}
      {title ? (
        <div className="flex w-full items-center">
          <StackedContent full center content={title} />
        </div>
      ) : (
        <div className="py-10"></div>
      )}
      {/* Carousel Items */}
      <div className="flex w-full snap-x flex-row items-start justify-start gap-5 overflow-x-scroll p-5 pb-0 sm:p-10 sm:pb-0">
        {sortedItems.map((item, index) => (
          <CarouselItem {...item} key={index + '-carouselitem'} />
        ))}
      </div>
    </motion.section>
  );
};

// Timeline Carousel --------------------------------
const TimelineCarouselItem = ({ img, date, copy }: TimelineCarouselItem) => {
  const { t } = useSanitizedTranslation();

  return (
    <div className="relative z-20 flex h-full w-max min-w-[325px] max-w-[325px] snap-center flex-col overflow-hidden rounded-xl bg-white sm:min-w-[400px] sm:max-w-[400px]">
      {/* Image */}
      <div className="relative h-[200px] w-full md:h-[260px]">
        <Image src={img.src} alt={img.alt} fill className="object-cover" />
      </div>

      {/* Text */}
      <div className="flex min-h-[300px] w-full flex-col gap-8 rounded-b-xl border-[1px] border-xumoDarkPlum p-6">
        <p className="type-card-heading">{date}</p>
        <div className="type-card-body-small mt-2 flex flex-col gap-6" data-i18n dangerouslySetInnerHTML={{ __html: t(copy) }} />
      </div>
    </div>
  );
};

export const TimelineCarousel = ({ bg, title, items, sectionClass = '', leadingGutterClass = '', trailingGutterClass = '', scrollRef }: any) => {
  const { t } = useSanitizedTranslation();

  const internalScrollerRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = scrollRef || internalScrollerRef;
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  // --- Scroll to specific card when indicator clicked
  const scrollToIndex = useCallback((index: number) => {
    const el = scrollerRef.current;
    const card = cardRefs.current[index];
    if (!el || !card) return;

    setIsUserScrolling(true);

    const cardLeft = card.offsetLeft;
    const cardWidth = card.offsetWidth;
    const containerWidth = el.clientWidth;
    const targetScroll = cardLeft - (containerWidth - cardWidth) / 2;

    el.scrollTo({
      left: Math.max(0, targetScroll),
      behavior: 'smooth',
    });

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => setIsUserScrolling(false), 600);
  }, []);

  const handleSelect = (index: number) => {
    scrollToIndex(index);
  };

  // --- Track scroll position and update indicator
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    let rafId = 0;

    const updateCurrentIndex = () => {
      const el = scrollerRef.current;
      if (!el) return;

      const scrollLeft = el.scrollLeft;
      const containerWidth = el.clientWidth;
      const scrollCenter = scrollLeft + containerWidth / 2;

      let closest = 0;
      let min = Infinity;

      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const d = Math.abs(scrollCenter - cardCenter);
        if (d < min) {
          min = d;
          closest = i;
        }
      });

      if (closest !== currentIndex) setCurrentIndex(closest);
    };

    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateCurrentIndex);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    updateCurrentIndex();

    return () => {
      el.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [currentIndex, isUserScrolling]);

  return (
    <div className={`flex flex-col items-start justify-center ${sectionClass}`} style={{ background: bg ?? 'white' }}>
      {title && (
        <div className="flex w-full items-center">
          <StackedContent full center content={title} />
        </div>
      )}

      {/* Scrollable cards */}
      <div ref={scrollerRef} className="hide-scroll flex w-full snap-x items-start gap-8 overflow-x-auto pr-12 md:snap-none md:pr-0">
        <div className={`shrink-0 ${leadingGutterClass}`} aria-hidden />
        {items.map((item: any, index: number) => (
          <div
            key={index}
            ref={el => {
              cardRefs.current[index] = el;
            }}
            className="shrink-0 snap-center"
            style={{
              scrollSnapAlign: 'center',
              scrollSnapStop: 'always',
            }}
          >
            <TimelineCarouselItem {...item} />
          </div>
        ))}
        <div className={`shrink-0  ${trailingGutterClass}`} aria-hidden />
      </div>

      {/* Scroll indicators */}
      <div className="z-20 mb-4 mt-8 flex w-full justify-center gap-2 md:hidden">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => handleSelect(index)}
            className={`h-3 w-3 rounded-full transition-all duration-300 ${currentIndex === index ? 'w-8 bg-xumoDarkPlum' : 'bg-xumoDarkPlum/30'}`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
